import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { Stack } from '@mui/system'
import { useEffect, useRef, useState } from 'react'
import {
    BarcodeTypeEnum,
    IvaEnum,
    NamedPrice,
    UnitType,
    useProduct,
} from '../../context/product_context'
import GenericModal from '../GenericModal'
import { StringField } from '../StringField'
import { UnitTypeField } from '../UnitTypeField'
import { CurrencyField } from '../CurrencyField'
import { PorcentageField } from '../PorcentageField'
import { SubtitleHeader } from '../SubtitleHeader'
import { getFileEndpoint } from '../../services/media'
import { FileSelection } from '../FileSelection'
import { FileList } from '../FileList'
import { ClientInfo } from '../../context/client_context'
import { ClientFilters } from '../../pages/Clients/Home'
import { useLoading } from '../../context/loading_context'
import { useSnack } from '../../context/snack_context'
import { GenericTable } from '../GenericTable'
import { listClients } from '../../services/client'
import { maskCurrency } from '../../utils/maks'
import { useCoin } from '../../context/coin_context'
import { SystemBranchEnum, useConfigs } from '../../context/configs_context'
import { useAccount } from '../../context/account_context'
import { useBranch } from '../../context/branch_context'
import { SupplierSearchBar } from '../SupplierSearchBar'
import { ProductSearchBar } from '../ProductSearchBar'
import { useTranslation } from 'react-i18next'
import { generateRandomBarcode } from '../../services/product'
import CloseIcon from '@mui/icons-material/Close'
import { ProductAdvancedSearch } from '../ProductAdvancedSearch'
import { StyledPaper } from '../StyledPaper'
const currency = require('currency.js')

interface Props {
    isEditing: boolean
    isMassEditing: boolean
}

export const Step1 = ({ isEditing, isMassEditing }: Props) => {
    const { t } = useTranslation()
    const loading = useLoading()
    const snack = useSnack()
    const {
        productInfo,
        setProductInfo,
        productErrorInfo,
        setProductErrorInfo,
        handleCreateNewGroup,
        handleCreateNewSubGroup,
        handleCreateNewMark,
        handleCreateNewModel,
        massEditSelectedFields,
        setMassEditSelectedFields,
    } = useProduct()
    const [openGroupModal, setOpenGroupModal] = useState(false)
    const [openSubGroupModal, setOpenSubGroupModal] = useState(false)
    const [openMarkModal, setOpenMarkModal] = useState(false)
    const [openModelModal, setOpenModelModal] = useState(false)

    const [newGroupName, setNewGroupName] = useState('')
    const [newSubGroupName, setNewSubGroupName] = useState('')
    const [newMarkName, setNewMarkName] = useState('')
    const [newModelName, setNewModelName] = useState('')

    const [openFileModal, setOpenFileModal] = useState(false)
    const [openPreviewModal, setOpenPreviewModal] = useState(false)

    const [clients, setClients] = useState<ClientInfo[]>([])
    const [selectedClients, setSelectedClients] = useState<ClientInfo[]>([])
    const [filters, setFilters] = useState<ClientFilters>({
        page: 1,
        rows: 10,
        rowsCount: 0,
    })
    const [search, setSearch] = useState('')
    const timeoutRef = useRef<NodeJS.Timeout>()

    const [openCreateNamedPriceModal, setOpenCreateNamedPriceModal] =
        useState(false)
    const [openAddClientsNamedPriceModal, setOpenAddClientsNamedPriceModal] =
        useState(false)

    const [editingPriceName, setEditingPriceName] = useState(false)
    const [newPriceName, setNewPriceName] = useState('')
    const [newPriceValue, setNewPriceValue] = useState(0)
    const [newPriceGain, setNewPriceGain] = useState(0)

    const [newPriceNameError, setNewPriceNameError] = useState('')
    const [newPriceValueError, setNewPriceValueError] = useState('')
    const [selectedNamedPrice, setSelectedNamedPrice] = useState<NamedPrice[]>(
        []
    )
    const { branches } = useBranch()
    const { coins } = useCoin()
    const { accountInfo } = useAccount()
    const { configs } = useConfigs()

    const canListClient =
        accountInfo?.isMaster ||
        accountInfo?.rules.findIndex(
            (r) =>
                r.group.toLowerCase() === 'clientes' &&
                r.name.toLowerCase() === 'listar'
        ) !== -1

    useEffect(() => {
        if (!configs.id) return

        if (configs.isInternalCodeIncremental && !isEditing && !isMassEditing)
            handleGenerateRandomCode(BarcodeTypeEnum.Internal, 0, true)
    }, [configs])

    useEffect(() => {
        if (canListClient) getClients(filters, search)
    }, [accountInfo])

    useEffect(() => {
        if (!coins.length || !accountInfo) return

        if (!productInfo.coin) {
            setProductInfo({
                ...productInfo,
                coin: accountInfo.branch?.coin ?? coins[0],
            })
        }
    }, [coins, productInfo, accountInfo])

    const getClients = async (
        filters: ClientFilters,
        search: string,
        dontSetEmpty?: boolean
    ) => {
        loading.logicShow()
        try {
            const res = await listClients(
                {
                    skip: filters.page,
                    take: filters.rows,
                    search: search.length ? search : undefined,
                },
                coins
            )

            if (!dontSetEmpty || res.data.length) {
                setFilters({
                    ...filters,
                    rowsCount: res.count,
                })
                setClients(res.data)
            }
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.logicHide()
    }

    const handleGenerateRandomCode = async (
        type: number,
        index: number,
        getLastInsertedId?: boolean
    ) => {
        loading.show()
        try {
            const newCodes =
                type === BarcodeTypeEnum.Internal
                    ? productInfo.internalCodes
                    : productInfo.barcodes
            newCodes[index] = await generateRandomBarcode(
                type,
                getLastInsertedId
            )
            setProductInfo((prev) => ({
                ...prev,
                [type === BarcodeTypeEnum.Internal
                    ? 'internalCodes'
                    : 'barcodes']: [...newCodes],
            }))
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }

    const canUpdateProduct =
        accountInfo?.isMaster ||
        accountInfo?.rules.findIndex(
            (r) =>
                r.group.toLowerCase() === 'produtos' &&
                r.name.toLowerCase() === 'atualizar'
        ) !== -1

    const canUpdateProductBarcode =
        accountInfo?.isMaster ||
        accountInfo?.rules.findIndex(
            (r) =>
                r.group.toLowerCase() === 'produtos' &&
                r.name.toLowerCase() === 'editar código de barras'
        ) !== -1

    const canUpdateProductInternalcode =
        accountInfo?.isMaster ||
        accountInfo?.rules.findIndex(
            (r) =>
                r.group.toLowerCase() === 'produtos' &&
                r.name.toLowerCase() === 'editar código interno'
        ) !== -1

    const canUpdateProductImage =
        accountInfo?.isMaster ||
        accountInfo?.rules.findIndex(
            (r) =>
                r.group.toLowerCase() === 'produtos' &&
                r.name.toLowerCase() === 'editar imagem'
        ) !== -1

    const canUpdateProductLocalization =
        accountInfo?.isMaster ||
        accountInfo?.rules.findIndex(
            (r) =>
                r.group.toLowerCase() === 'produtos' &&
                r.name.toLowerCase() === 'editar localização'
        ) !== -1

    const canChangeCoin =
        (accountInfo?.isMaster ||
            !!accountInfo?.permissions?.includes('Moedas:Modificar moeda')) &&
        !isEditing

    const components = [
        {
            component: (
                <>
                    <SubtitleHeader>{t('Datos generales')}</SubtitleHeader>
                    <Grid container spacing={2} sx={{ mb: '16px' }}>
                        <Grid item xs={12} md={6}>
                            <StringField
                                label="Nombre"
                                maxLength={100}
                                value={productInfo.name}
                                setValue={(v: string) => {
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        name: v,
                                    }))
                                }}
                                sx={{ width: '100%' }}
                                error={productErrorInfo.name}
                                onClick={() => {
                                    setProductErrorInfo((prev) => ({
                                        ...prev,
                                        name: '',
                                    }))
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <StringField
                                error={productErrorInfo.observation}
                                onClick={() => {
                                    setProductErrorInfo((prev) => ({
                                        ...prev,
                                        observation: '',
                                    }))
                                }}
                                label="Observación"
                                maxLength={100}
                                value={productInfo.observation ?? ''}
                                setValue={(v: string) => {
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        observation: v,
                                    }))
                                }}
                                sx={{ width: '100%' }}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: '16px' }}>
                        <Grid item xs={12} md={6}>
                            <FormControl sx={{ width: '100%' }}>
                                <InputLabel>{t('Grupo')}</InputLabel>
                                <Select
                                    label={t('Grupo')}
                                    error={Boolean(
                                        productErrorInfo.selectedGroup
                                    )}
                                    onClick={() => {
                                        setProductErrorInfo((prev) => ({
                                            ...prev,
                                            selectedGroup: '',
                                        }))
                                    }}
                                    IconComponent={() => null}
                                    fullWidth
                                    endAdornment={
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            sx={{
                                                textTransform: 'none',
                                                whiteSpace: 'nowrap',
                                            }}
                                            onClick={() =>
                                                setOpenGroupModal(true)
                                            }
                                        >
                                            {t('Nuevo')}
                                        </Button>
                                    }
                                    value={
                                        productInfo.selectedGroup
                                            ? productInfo.selectedGroup.id
                                            : ''
                                    }
                                    onChange={(e) => {
                                        const { value } = e.target
                                        console.log(value)
                                        if (!value) {
                                            setProductInfo((prev) => ({
                                                ...prev,
                                                selectedGroup: null,
                                                subGroups: null,
                                                selectedSubGroup: null,
                                            }))
                                            return
                                        }

                                        const group = productInfo.groups.find(
                                            (g) => g.id === value
                                        )
                                        group &&
                                            setProductInfo((prev) => ({
                                                ...prev,
                                                selectedGroup: group,
                                                subGroups: null,
                                                selectedSubGroup: null,
                                            }))
                                    }}
                                >
                                    {productInfo.selectedGroup && (
                                        <MenuItem value={''}>
                                            {t('Todos los grupos')}
                                        </MenuItem>
                                    )}
                                    {productInfo.groups.map((group) => (
                                        <MenuItem value={group.id}>
                                            {group.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText error>
                                    {productErrorInfo.selectedGroup}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl sx={{ width: '100%' }}>
                                <InputLabel>{t('Subgrupo')}</InputLabel>
                                <Select
                                    label={t('Subgrupo')}
                                    error={Boolean(
                                        productErrorInfo.selectedSubGroup
                                    )}
                                    onClick={() => {
                                        setProductErrorInfo((prev) => ({
                                            ...prev,
                                            selectedSubGroup: '',
                                        }))
                                    }}
                                    IconComponent={() => null}
                                    fullWidth
                                    endAdornment={
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            sx={{
                                                textTransform: 'none',
                                                whiteSpace: 'nowrap',
                                            }}
                                            disabled={
                                                !productInfo.selectedGroup
                                            }
                                            onClick={() =>
                                                setOpenSubGroupModal(true)
                                            }
                                        >
                                            {t('Nuevo')}
                                        </Button>
                                    }
                                    value={
                                        productInfo.selectedSubGroup
                                            ? productInfo.selectedSubGroup.id
                                            : ''
                                    }
                                    onChange={(e) => {
                                        const { value } = e.target
                                        if (!value) {
                                            setProductInfo((prev) => ({
                                                ...prev,
                                                selectedSubGroup: null,
                                            }))
                                            return
                                        }

                                        const subGroup =
                                            productInfo.subGroups?.find(
                                                (sg) => sg.id === value
                                            )
                                        subGroup &&
                                            setProductInfo((prev) => ({
                                                ...prev,
                                                selectedSubGroup: subGroup,
                                            }))
                                    }}
                                >
                                    {productInfo.selectedSubGroup && (
                                        <MenuItem value={''}>
                                            {t('Todos los subgrupos')}
                                        </MenuItem>
                                    )}
                                    {productInfo.subGroups?.map((subGroup) => (
                                        <MenuItem value={subGroup.id}>
                                            {subGroup.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText error>
                                    {productErrorInfo.selectedSubGroup}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: '16px' }}>
                        <Grid item xs={12} md={6}>
                            <FormControl sx={{ width: '100%' }}>
                                <InputLabel>{t('Marca')}</InputLabel>
                                <Select
                                    label={t('Marca')}
                                    error={Boolean(
                                        productErrorInfo.selectedMark
                                    )}
                                    onClick={() => {
                                        setProductErrorInfo((prev) => ({
                                            ...prev,
                                            selectedMark: '',
                                        }))
                                    }}
                                    IconComponent={() => null}
                                    fullWidth
                                    endAdornment={
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            sx={{
                                                textTransform: 'none',
                                                whiteSpace: 'nowrap',
                                            }}
                                            onClick={() =>
                                                setOpenMarkModal(true)
                                            }
                                        >
                                            {t('Nuevo')}
                                        </Button>
                                    }
                                    value={
                                        productInfo.selectedMark
                                            ? productInfo.selectedMark.id
                                            : ''
                                    }
                                    onChange={(e) => {
                                        const { value } = e.target
                                        if (!value) {
                                            setProductInfo((prev) => ({
                                                ...prev,
                                                selectedMark: null,
                                                models: null,
                                                selectedModel: null,
                                            }))
                                            return
                                        }

                                        const mark = productInfo.marks.find(
                                            (m) => m.id === value
                                        )
                                        mark &&
                                            setProductInfo((prev) => ({
                                                ...prev,
                                                selectedMark: mark,
                                                models: null,
                                                selectedModel: null,
                                            }))
                                    }}
                                >
                                    {productInfo.selectedMark && (
                                        <MenuItem value={''}>
                                            {t('Todas las marcas')}
                                        </MenuItem>
                                    )}
                                    {productInfo.marks.map((mark) => (
                                        <MenuItem value={mark.id}>
                                            {mark.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText error>
                                    {productErrorInfo.selectedMark}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl sx={{ width: '100%' }}>
                                <InputLabel>{t('Modelo')}</InputLabel>
                                <Select
                                    label={t('Modelo')}
                                    error={Boolean(
                                        productErrorInfo.selectedModel
                                    )}
                                    onClick={() => {
                                        setProductErrorInfo((prev) => ({
                                            ...prev,
                                            selectedModel: '',
                                        }))
                                    }}
                                    IconComponent={() => null}
                                    fullWidth
                                    endAdornment={
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            sx={{
                                                textTransform: 'none',
                                                whiteSpace: 'nowrap',
                                            }}
                                            disabled={!productInfo.selectedMark}
                                            onClick={() =>
                                                setOpenModelModal(true)
                                            }
                                        >
                                            {t('Nuevo')}
                                        </Button>
                                    }
                                    value={
                                        productInfo.selectedModel
                                            ? productInfo.selectedModel.id
                                            : ''
                                    }
                                    onChange={(e) => {
                                        const { value } = e.target
                                        if (!value) {
                                            setProductInfo((prev) => ({
                                                ...prev,
                                                selectedModel: null,
                                            }))
                                            return
                                        }

                                        const model = productInfo.models?.find(
                                            (m) => m.id === value
                                        )
                                        model &&
                                            setProductInfo((prev) => ({
                                                ...prev,
                                                selectedModel: model,
                                            }))
                                    }}
                                >
                                    {productInfo.selectedModel && (
                                        <MenuItem value={''}>
                                            {t('Todos los modelos')}
                                        </MenuItem>
                                    )}
                                    {productInfo.models?.map((model) => (
                                        <MenuItem value={model.id}>
                                            {model.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText error>
                                    {productErrorInfo.selectedModel}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mb: '16px' }}>
                        <Grid item xs={12} md={6}>
                            <SupplierSearchBar
                                supplier={productInfo.supplier}
                                returnSupplier={(s) =>
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        supplier: s,
                                    }))
                                }
                                sx={{ width: '100%' }}
                                size="medium"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <StringField
                                label="Solicitud"
                                maxLength={100}
                                value={productInfo.aplication ?? ''}
                                setValue={(v: string) => {
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        aplication: v,
                                    }))
                                }}
                                sx={{ width: '100%' }}
                                error={productErrorInfo.aplication}
                                onClick={() => {
                                    setProductErrorInfo((prev) => ({
                                        ...prev,
                                        aplication: '',
                                    }))
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mb: '16px' }}>
                        <Grid item xs={12} md={6}>
                            <ProductSearchBar
                                selectedProduct={productInfo.noteProduct}
                                returnProducts={(p) => {
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        noteProduct: p[0] ?? null,
                                    }))
                                }}
                                customLabel="Producto factura"
                                size="medium"
                                sx={{ width: '100%' }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <StringField
                                label="NCM"
                                maxLength={255}
                                value={productInfo.ncm ?? ''}
                                setValue={(v: string) => {
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        ncm: v,
                                    }))
                                }}
                                sx={{ width: '100%' }}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: '16px' }}>
                        <Grid item xs={12} md={6}>
                            <UnitTypeField
                                sx={{ width: '100%' }}
                                label="Peso"
                                value={productInfo.realWeight}
                                setValue={(v) => {
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        realWeight: v,
                                    }))
                                }}
                                unitType="Kg"
                            />
                        </Grid>
                        {canChangeCoin && (
                            <Grid item xs={12} md={6}>
                                <FormControl
                                    sx={{ width: '100%' }}
                                    disabled={isEditing}
                                >
                                    <InputLabel>{t('Moneda')}</InputLabel>
                                    <Select
                                        label={t('Moneda')}
                                        fullWidth
                                        value={productInfo.coin?.id ?? ''}
                                        onChange={(e) => {
                                            const { value } = e.target
                                            if (!value) return
                                            const coin = coins.find(
                                                (c) => c.id === value
                                            )
                                            if (coin) {
                                                setProductInfo((prev) => ({
                                                    ...prev,
                                                    coin: coin,
                                                }))
                                            }
                                        }}
                                    >
                                        {coins.map((coin) => (
                                            <MenuItem value={coin.id}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={`/flags/${coin.pathImage}`}
                                                        sx={{
                                                            width: '25px',
                                                            height: '25px',
                                                            borderRadius: '50%',
                                                        }}
                                                    />
                                                    <Typography
                                                        sx={{ ml: '15px' }}
                                                    >
                                                        {coin.name} ({coin.iso})
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>
                </>
            ),
            show: !isEditing || canUpdateProduct,
        },
        {
            component: (
                <>
                    <SubtitleHeader sx={{ mt: '20px' }}>
                        {t('Códigos de barra')}
                    </SubtitleHeader>
                    {productInfo.barcodes.map((barcode, index) => (
                        <Stack flexDirection="row" key={index}>
                            <StringField
                                error={productErrorInfo.barcodes[index]}
                                onClick={() => {
                                    const newBarcodesError =
                                        productErrorInfo.barcodes
                                    newBarcodesError[index] = ''
                                    setProductErrorInfo((prev) => ({
                                        ...prev,
                                        barcodes: newBarcodesError,
                                    }))
                                }}
                                label="Código de barra"
                                maxLength={45}
                                value={barcode}
                                setValue={(v: string) => {
                                    const newBarcodes = productInfo.barcodes
                                    newBarcodes[index] = v
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        barcodes: [...newBarcodes],
                                    }))
                                }}
                                endAdornment={
                                    <Button
                                        size="small"
                                        variant="text"
                                        sx={{
                                            textTransform: 'none',
                                            whiteSpace: 'nowrap',
                                        }}
                                        onClick={() => {
                                            handleGenerateRandomCode(
                                                BarcodeTypeEnum.Barcode,
                                                index
                                            )
                                        }}
                                    >
                                        {t('Autovalor')}
                                    </Button>
                                }
                                sx={{
                                    width: { xs: '80%', md: '47%' },
                                    mb: '15px',
                                }}
                            />
                            {index > 0 && (
                                <Button
                                    color="error"
                                    variant="contained"
                                    sx={{ ml: '20px', maxHeight: '56px' }}
                                    onClick={() => {
                                        const newBarcodes = productInfo.barcodes
                                        newBarcodes.splice(index, 1)
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            barcodes: [...newBarcodes],
                                        }))
                                        setProductErrorInfo((prev) => ({
                                            ...prev,
                                            barcodes: [],
                                        }))
                                    }}
                                >
                                    -
                                </Button>
                            )}
                            {index === productInfo.barcodes.length - 1 && (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    sx={{ ml: '20px', maxHeight: '56px' }}
                                    onClick={() => {
                                        const newBarcodes = productInfo.barcodes
                                        newBarcodes.push('')
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            barcodes: [...newBarcodes],
                                        }))
                                        setProductErrorInfo((prev) => ({
                                            ...prev,
                                            barcodes: [],
                                        }))
                                    }}
                                >
                                    +
                                </Button>
                            )}
                        </Stack>
                    ))}
                </>
            ),
            show: !isEditing || canUpdateProduct || canUpdateProductBarcode,
        },
        {
            component: (
                <>
                    <SubtitleHeader>{t('Códigos internos')}</SubtitleHeader>
                    {productInfo.internalCodes.map((internalCode, index) => (
                        <Stack flexDirection="row" key={index}>
                            <StringField
                                error={productErrorInfo.internalCodes[index]}
                                onClick={() => {
                                    const newInternalCodes =
                                        productErrorInfo.internalCodes
                                    newInternalCodes[index] = ''
                                    setProductErrorInfo((prev) => ({
                                        ...prev,
                                        internalCodes: newInternalCodes,
                                    }))
                                }}
                                label="Código interno"
                                maxLength={45}
                                value={internalCode}
                                setValue={(v: string) => {
                                    const newInternalCodes =
                                        productInfo.internalCodes
                                    newInternalCodes[index] = v
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        internalCodes: [...newInternalCodes],
                                    }))
                                }}
                                endAdornment={
                                    <Button
                                        size="small"
                                        variant="text"
                                        sx={{
                                            textTransform: 'none',
                                            whiteSpace: 'nowrap',
                                        }}
                                        onClick={async () => {
                                            handleGenerateRandomCode(
                                                BarcodeTypeEnum.Internal,
                                                index
                                            )
                                        }}
                                    >
                                        {t('Autovalor')}
                                    </Button>
                                }
                                sx={{
                                    width: { xs: '80%', md: '47%' },
                                    mb: '15px',
                                }}
                            />
                            {index > 0 && (
                                <Button
                                    color="error"
                                    variant="contained"
                                    sx={{ ml: '20px', maxHeight: '56px' }}
                                    onClick={() => {
                                        const newInternalCodes =
                                            productInfo.internalCodes
                                        newInternalCodes.splice(index, 1)
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            internalCodes: [
                                                ...newInternalCodes,
                                            ],
                                        }))
                                        setProductErrorInfo((prev) => ({
                                            ...prev,
                                            internalCodes: [],
                                        }))
                                    }}
                                >
                                    -
                                </Button>
                            )}
                            {index === productInfo.internalCodes.length - 1 && (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    sx={{ ml: '20px', maxHeight: '56px' }}
                                    onClick={() => {
                                        const newInternalCodes =
                                            productInfo.internalCodes
                                        newInternalCodes.push('')
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            internalCodes: [
                                                ...newInternalCodes,
                                            ],
                                        }))
                                        setProductErrorInfo((prev) => ({
                                            ...prev,
                                            internalCodes: [],
                                        }))
                                    }}
                                >
                                    +
                                </Button>
                            )}
                        </Stack>
                    ))}
                </>
            ),
            show:
                !isEditing || canUpdateProduct || canUpdateProductInternalcode,
        },
        {
            component: (
                <>
                    <SubtitleHeader>{t('Precios')}</SubtitleHeader>
                    <Grid container spacing={2} sx={{ mb: '16px' }}>
                        <Grid item xs={12} md={6}>
                            <CurrencyField
                                error={productErrorInfo.buyPrice}
                                onClick={() => {
                                    setProductErrorInfo((prev) => ({
                                        ...prev,
                                        buyPrice: '',
                                    }))
                                }}
                                label="Precio de compra"
                                value={productInfo.buyPrice}
                                setValue={(v: number) => {
                                    const cifDiff = currency(
                                        productInfo.cifPrice
                                    ).subtract(productInfo.buyPrice).value

                                    let newSalePrice =
                                        v + (v * productInfo.porcentGain) / 100

                                    if (productInfo.coin?.hideDecimal) {
                                        newSalePrice = Math.round(newSalePrice)
                                    }

                                    const newNamedPrice = [
                                        ...productInfo.namedPrice,
                                    ]
                                    for (const namedPrice of newNamedPrice) {
                                        namedPrice.price =
                                            v +
                                            (v * namedPrice.porcentGain) / 100

                                        if (productInfo.coin?.hideDecimal) {
                                            namedPrice.price = Math.round(
                                                namedPrice.price
                                            )
                                        }
                                    }

                                    setProductInfo((prev) => ({
                                        ...prev,
                                        salePrice: newSalePrice,
                                        buyPrice: v,
                                        cifPrice:
                                            currency(v).add(cifDiff).value,
                                        namedPrice: newNamedPrice,
                                    }))
                                }}
                                sx={{ width: '100%' }}
                                coin={productInfo.coin}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <PorcentageField
                                error={productErrorInfo.porcentGain}
                                onClick={() => {
                                    setProductErrorInfo((prev) => ({
                                        ...prev,
                                        porcentGain: '',
                                    }))
                                }}
                                label="Porcentaje de ganancia"
                                value={productInfo.porcentGain}
                                setValue={(v: number) => {
                                    if (productInfo.buyPrice) {
                                        let newSalePrice =
                                            productInfo.buyPrice +
                                            (productInfo.buyPrice * v) / 100

                                        if (productInfo.coin?.hideDecimal) {
                                            newSalePrice =
                                                Math.round(newSalePrice)
                                        }

                                        setProductInfo((prev) => ({
                                            ...prev,
                                            salePrice: newSalePrice,
                                            porcentGain: v,
                                        }))
                                        return
                                    }
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        porcentGain: v,
                                    }))
                                }}
                                sx={{ width: '100%' }}
                                allowNegative
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: '16px' }}>
                        <Grid item xs={12} md={6}>
                            <CurrencyField
                                error={productErrorInfo.salePrice}
                                onClick={() => {
                                    setProductErrorInfo((prev) => ({
                                        ...prev,
                                        salePrice: '',
                                    }))
                                }}
                                label="Precio de venta"
                                value={productInfo.salePrice}
                                setValue={(v: number) => {
                                    if (productInfo.buyPrice) {
                                        const newPercentageGain =
                                            ((v - productInfo.buyPrice) * 100) /
                                            productInfo.buyPrice
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            porcentGain: Number(
                                                newPercentageGain.toFixed(2)
                                            ),
                                            salePrice: v,
                                        }))
                                        return
                                    }
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        salePrice: v,
                                    }))
                                }}
                                sx={{ width: '100%' }}
                                coin={productInfo.coin}
                            />
                        </Grid>
                        {configs.isCifPriceEditable && (
                            <Grid item xs={12} md={6}>
                                <CurrencyField
                                    label="Precio de compra CIF"
                                    value={productInfo.cifPrice}
                                    setValue={(v: number) => {
                                        if (productInfo.buyPrice) {
                                            setProductInfo((prev) => ({
                                                ...prev,
                                                cifPrice: v,
                                            }))
                                            return
                                        }
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            cifPrice: v,
                                        }))
                                    }}
                                    sx={{ width: '100%' }}
                                    onBlur={() => {
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            cifPrice:
                                                prev.cifPrice < prev.buyPrice
                                                    ? prev.buyPrice
                                                    : prev.cifPrice,
                                        }))
                                    }}
                                    coin={productInfo.coin}
                                />
                            </Grid>
                        )}
                    </Grid>
                    {productInfo.qntPrice.map((qntPrice, index) => (
                        <Stack
                            flexDirection="row"
                            sx={{ my: '15px' }}
                            key={index}
                        >
                            <CurrencyField
                                error={productErrorInfo.qntPrice[index]}
                                onClick={() => {
                                    const newQntPricesErrors =
                                        productErrorInfo.qntPrice
                                    newQntPricesErrors[index] = ''
                                    setProductErrorInfo((prev) => ({
                                        ...prev,
                                        qntPrice: newQntPricesErrors,
                                    }))
                                }}
                                label="Precio de venda em cantidad"
                                value={qntPrice.price}
                                setValue={(v: number) => {
                                    const newQntPrices = productInfo.qntPrice
                                    newQntPrices[index].price = v
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        qntPrice: [...newQntPrices],
                                    }))
                                }}
                                sx={{ width: { xs: '100%', md: '47%' } }}
                                coin={productInfo.coin}
                            />
                            <UnitTypeField
                                error={productErrorInfo.qntPrice[index]}
                                onClick={() => {
                                    const newQntPricesErrors =
                                        productErrorInfo.qntPrice
                                    newQntPricesErrors[index] = ''
                                    setProductErrorInfo((prev) => ({
                                        ...prev,
                                        qntPrice: newQntPricesErrors,
                                    }))
                                }}
                                label="Cantidad"
                                value={qntPrice.qnt}
                                setValue={(v: number) => {
                                    const newQntPrices = productInfo.qntPrice
                                    newQntPrices[index].qnt = v
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        qntPrice: [...newQntPrices],
                                    }))
                                }}
                                sx={{
                                    width: { xs: '25%', md: '15%' },
                                    ml: '20px',
                                }}
                                unitType={productInfo.unitType}
                            />

                            {index > 0 && (
                                <Button
                                    color="error"
                                    variant="contained"
                                    sx={{ ml: '20px', maxHeight: '56px' }}
                                    onClick={() => {
                                        const newQntPrices =
                                            productInfo.qntPrice
                                        newQntPrices.splice(index, 1)
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            qntPrice: [...newQntPrices],
                                        }))
                                    }}
                                >
                                    -
                                </Button>
                            )}
                            {index === productInfo.qntPrice.length - 1 && (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    sx={{ ml: '20px', maxHeight: '56px' }}
                                    onClick={() => {
                                        const newQntPrices =
                                            productInfo.qntPrice
                                        newQntPrices.push({ price: 0, qnt: 0 })
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            qntPrice: [...newQntPrices],
                                        }))
                                    }}
                                >
                                    +
                                </Button>
                            )}
                        </Stack>
                    ))}
                    <SubtitleHeader>{t('Lista de precios')}</SubtitleHeader>
                    <GenericTable
                        withoutPaper
                        hideSearch
                        hidePagination
                        title="cfe7f1c6-a6ff-47ec-9111-f09a3f5ee9de"
                        filters={[]}
                        search={''}
                        handleChangeSearch={() => {}}
                        handleChangeSelectedFilter={() => {}}
                        handleChangeSelectedValues={(values) => {
                            setSelectedNamedPrice(
                                values.map((value) => {
                                    const foundValue = selectedNamedPrice.find(
                                        (sv) => sv.name === value
                                    )
                                    if (foundValue) return foundValue
                                    else
                                        return productInfo.namedPrice.filter(
                                            (v) => v.name === value
                                        )[0]
                                })
                            )
                        }}
                        selectedValues={selectedNamedPrice.map(
                            (p) => p.name as string
                        )}
                        identifier="name"
                        values={productInfo.namedPrice}
                        subValueField={['clients']}
                        subValueIndentifier="id"
                        columns={[
                            {
                                name: 'Número',
                                property: ['name'],
                            },
                            {
                                name: 'Valor',
                                property: ['price'],
                                customRender: (
                                    namedPrice: NamedPrice,
                                    value
                                ) => {
                                    if (typeof value !== 'number') return <></>

                                    return (
                                        <CurrencyField
                                            label=""
                                            value={namedPrice.price}
                                            setValue={(v) => {
                                                const newPercentageGain =
                                                    ((v -
                                                        productInfo.buyPrice) *
                                                        100) /
                                                    productInfo.buyPrice

                                                const newNamedPrices = [
                                                    ...productInfo.namedPrice,
                                                ]
                                                const newNamedPrice =
                                                    newNamedPrices.find(
                                                        (np) =>
                                                            np.name ===
                                                            namedPrice.name
                                                    )
                                                if (!newNamedPrice) return

                                                newNamedPrice.price = v
                                                newNamedPrice.porcentGain =
                                                    newPercentageGain

                                                setProductInfo((prev) => ({
                                                    ...prev,
                                                    namedPrice: newNamedPrices,
                                                }))
                                            }}
                                            coin={productInfo.coin}
                                            size="small"
                                        />
                                    )
                                },
                            },
                            {
                                name: 'Porc lucro',
                                property: ['porcentGain'],
                                customRender: (
                                    namedPrice: NamedPrice,
                                    value
                                ) => {
                                    if (typeof value !== 'number') return <></>

                                    return (
                                        <PorcentageField
                                            label=""
                                            value={namedPrice.porcentGain}
                                            setValue={(v) => {
                                                let newSalePrice =
                                                    productInfo.buyPrice +
                                                    (productInfo.buyPrice * v) /
                                                        100

                                                if (
                                                    productInfo.coin
                                                        ?.hideDecimal
                                                ) {
                                                    newSalePrice =
                                                        Math.round(newSalePrice)
                                                }

                                                const newNamedPrices = [
                                                    ...productInfo.namedPrice,
                                                ]
                                                const newNamedPrice =
                                                    newNamedPrices.find(
                                                        (np) =>
                                                            np.name ===
                                                            namedPrice.name
                                                    )
                                                if (!newNamedPrice) return

                                                newNamedPrice.price =
                                                    newSalePrice
                                                newNamedPrice.porcentGain = v

                                                setProductInfo((prev) => ({
                                                    ...prev,
                                                    namedPrice: newNamedPrices,
                                                }))
                                            }}
                                            size="small"
                                            allowNegative
                                        />
                                    )
                                },
                            },
                            {
                                name: 'Nombre cliente',
                                property: ['clientName'],
                            },
                            {
                                name: 'Documento cliente',
                                property: ['document'],
                            },
                        ]}
                        actions={[
                            /* {
                                name: 'Seleccionar clientes',
                                color: 'success',
                                hide: !canListClient,
                            }, */
                            {
                                name: 'Remover',
                                color: 'error',
                            },
                            {
                                name: 'Nuevo',
                                color: 'primary',
                            },
                        ]}
                        handleExecuteAction={(action) => {
                            if (
                                action === 'Seleccionar clientes' &&
                                selectedNamedPrice.length
                            ) {
                                setOpenAddClientsNamedPriceModal(true)
                                setSelectedClients(
                                    selectedNamedPrice[0].clients
                                )
                            }
                            if (
                                action === 'Remover' &&
                                selectedNamedPrice.length
                            ) {
                                setProductInfo((prev) => ({
                                    ...prev,
                                    namedPrice: [
                                        ...prev.namedPrice.filter(
                                            (np) =>
                                                !selectedNamedPrice.find(
                                                    (snp) =>
                                                        snp.name === np.name
                                                )
                                        ),
                                    ],
                                }))
                                setSelectedNamedPrice([])
                            }
                            /* if (action === 'Editar' && selectedNamedPrice.length) {
                        setEditingPriceName(true)
                        setNewPriceName(selectedNamedPrice[0].name)
                        setNewPriceValue(selectedNamedPrice[0].price)
                        setNewPriceGain(selectedNamedPrice[0].porcentGain)
                        setOpenCreateNamedPriceModal(true)
                    } */
                            if (action === 'Nuevo') {
                                setEditingPriceName(false)
                                setNewPriceName('')
                                setNewPriceValue(0)
                                setNewPriceGain(0)
                                setOpenCreateNamedPriceModal(true)
                            }
                        }}
                    />
                    <SubtitleHeader>{t('Unidad de medida')}</SubtitleHeader>
                    <FormControl
                        sx={{
                            width: {
                                xs: '100%',
                            },
                            mb: '15px',
                        }}
                    >
                        <RadioGroup
                            row
                            onChange={(e) => {
                                const { value } = e.target
                                setProductInfo((prev) => ({
                                    ...prev,
                                    unitType: value,
                                }))
                            }}
                            value={productInfo.unitType}
                            sx={{
                                display: 'flex',
                            }}
                        >
                            <FormControlLabel
                                value={UnitType.Unit}
                                control={<Radio />}
                                label={t('Unitário')}
                            />
                            <FormControlLabel
                                value={UnitType.Kg}
                                control={<Radio />}
                                label="Kg"
                            />
                            <FormControlLabel
                                value={UnitType['Mt/m2']}
                                control={<Radio />}
                                label="Mt/m2"
                            />
                        </RadioGroup>
                    </FormControl>
                    {!accountInfo?.branch &&
                        configs.systemBranch === SystemBranchEnum.Branch && (
                            <>
                                <SubtitleHeader>
                                    {t('Sucursales')}
                                </SubtitleHeader>
                                {productInfo.branches.map((branch, index) => (
                                    <Stack
                                        flexDirection="row"
                                        key={index}
                                        sx={{ mb: '20px' }}
                                    >
                                        {
                                            <FormControl
                                                sx={{
                                                    width: {
                                                        xs: '100%',
                                                        md: '47%',
                                                    },
                                                }}
                                            >
                                                <InputLabel>
                                                    {t('Sucursal')}
                                                </InputLabel>
                                                <Select
                                                    label={t('Sucursal')}
                                                    value={
                                                        branch?.id
                                                            ? branch?.id
                                                            : ''
                                                    }
                                                    fullWidth
                                                    onChange={(e) => {
                                                        const foundBranch =
                                                            branches.find(
                                                                (b) =>
                                                                    b.id ===
                                                                    Number(
                                                                        e.target
                                                                            .value
                                                                    )
                                                            ) ?? null
                                                        const newBranches =
                                                            productInfo.branches
                                                        newBranches[index] =
                                                            foundBranch
                                                        setProductInfo(
                                                            (prev) => ({
                                                                ...prev,
                                                                branches: [
                                                                    ...newBranches,
                                                                ],
                                                            })
                                                        )
                                                    }}
                                                >
                                                    {!!branch && (
                                                        <MenuItem
                                                            value={undefined}
                                                        >
                                                            {t('Sin sucursal')}
                                                        </MenuItem>
                                                    )}
                                                    {branches.map((branch) => (
                                                        <MenuItem
                                                            value={branch.id}
                                                        >
                                                            {branch.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        }
                                        {index > 0 && (
                                            <Button
                                                color="error"
                                                variant="contained"
                                                sx={{
                                                    ml: '20px',
                                                    maxHeight: '56px',
                                                }}
                                                onClick={() => {
                                                    const newBranches =
                                                        productInfo.branches
                                                    newBranches.splice(index, 1)
                                                    setProductInfo((prev) => ({
                                                        ...prev,
                                                        branches: [
                                                            ...newBranches,
                                                        ],
                                                    }))
                                                }}
                                            >
                                                -
                                            </Button>
                                        )}
                                        {index ===
                                            productInfo.branches.length - 1 && (
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                sx={{
                                                    ml: '20px',
                                                    maxHeight: '56px',
                                                }}
                                                onClick={() => {
                                                    const newBranches =
                                                        productInfo.branches
                                                    newBranches.push(null)
                                                    setProductInfo((prev) => ({
                                                        ...prev,
                                                        branches: [
                                                            ...newBranches,
                                                        ],
                                                    }))
                                                }}
                                            >
                                                +
                                            </Button>
                                        )}
                                    </Stack>
                                ))}
                            </>
                        )}
                </>
            ),
            show: !isEditing || canUpdateProduct,
        },
        {
            component: (
                <>
                    <SubtitleHeader>{t('Localizaciónes')}</SubtitleHeader>
                    {productInfo.localizations.map((localization, index) => (
                        <Stack flexDirection="row" key={index}>
                            <StringField
                                label="Valor"
                                maxLength={100}
                                value={localization.value}
                                setValue={(v: string) => {
                                    const newLocalizations =
                                        productInfo.localizations
                                    newLocalizations[index].value = v
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        localizations: [...newLocalizations],
                                    }))
                                }}
                                sx={{
                                    width: { xs: '80%', md: '47%' },
                                    mb: '15px',
                                }}
                            />
                            {!accountInfo?.branch &&
                                configs.systemBranch ===
                                    SystemBranchEnum.Branch && (
                                    <FormControl
                                        sx={{
                                            width: { xs: '100%', md: '30%' },
                                            ml: '20px',
                                        }}
                                    >
                                        <InputLabel>{t('Sucursal')}</InputLabel>
                                        <Select
                                            label={t('Sucursal')}
                                            value={
                                                localization.branchId
                                                    ? localization.branchId
                                                    : ''
                                            }
                                            fullWidth
                                            onChange={(e) => {
                                                const newLocalizations =
                                                    productInfo.localizations
                                                newLocalizations[
                                                    index
                                                ].branchId = e.target.value
                                                    ? Number(e.target.value)
                                                    : undefined
                                                setProductInfo((prev) => ({
                                                    ...prev,
                                                    localizations: [
                                                        ...newLocalizations,
                                                    ],
                                                }))
                                            }}
                                        >
                                            {localization.branchId && (
                                                <MenuItem value={undefined}>
                                                    {t('Sin sucursal')}
                                                </MenuItem>
                                            )}
                                            {branches.map((branch) => (
                                                <MenuItem value={branch.id}>
                                                    {branch.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            {index > 0 && (
                                <Button
                                    color="error"
                                    variant="contained"
                                    sx={{ ml: '20px', maxHeight: '56px' }}
                                    onClick={() => {
                                        const newLocalizations =
                                            productInfo.localizations
                                        newLocalizations.splice(index, 1)
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            localizations: [
                                                ...newLocalizations,
                                            ],
                                        }))
                                    }}
                                >
                                    -
                                </Button>
                            )}
                            {index === productInfo.localizations.length - 1 && (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    sx={{ ml: '20px', maxHeight: '56px' }}
                                    onClick={() => {
                                        const newLocalizations =
                                            productInfo.localizations
                                        newLocalizations.push({
                                            value: '',
                                        })
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            localizations: [
                                                ...newLocalizations,
                                            ],
                                        }))
                                    }}
                                >
                                    +
                                </Button>
                            )}
                        </Stack>
                    ))}
                </>
            ),
            show:
                !isEditing || canUpdateProduct || canUpdateProductLocalization,
        },
        {
            component: (
                <>
                    <SubtitleHeader> {t('Carga pós venta')}</SubtitleHeader>
                    {productInfo.productsStockAfterOperation.map((v, index) => (
                        <Stack flexDirection="row" key={index}>
                            <ProductSearchBar
                                selectedProduct={v.product}
                                returnProducts={(p) => {
                                    const newValue =
                                        productInfo.productsStockAfterOperation
                                    newValue[index].product = p[0]
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        productsStockAfterOperation: [
                                            ...newValue,
                                        ],
                                    }))
                                }}
                                size="medium"
                                sx={{
                                    width: { xs: '80%', md: '47%' },
                                    mb: '15px',
                                }}
                            />
                            <UnitTypeField
                                label="Cantidad"
                                value={v.qnt}
                                setValue={(v) => {
                                    const newValue =
                                        productInfo.productsStockAfterOperation
                                    newValue[index].qnt = v
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        productsStockAfterOperation: [
                                            ...newValue,
                                        ],
                                    }))
                                }}
                                sx={{
                                    width: { xs: '100%', md: '30%' },
                                    ml: '20px',
                                }}
                                unitType={v.product?.unitType ?? 'Un'}
                            />

                            {index > 0 && (
                                <Button
                                    color="error"
                                    variant="contained"
                                    sx={{ ml: '20px', maxHeight: '56px' }}
                                    onClick={() => {
                                        const newValue =
                                            productInfo.productsStockAfterOperation
                                        newValue.splice(index, 1)
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            productsStockAfterOperation: [
                                                ...newValue,
                                            ],
                                        }))
                                    }}
                                >
                                    -
                                </Button>
                            )}
                            {index ===
                                productInfo.productsStockAfterOperation.length -
                                    1 && (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    sx={{ ml: '20px', maxHeight: '56px' }}
                                    onClick={() => {
                                        const newValue =
                                            productInfo.productsStockAfterOperation
                                        newValue.push({
                                            product: null,
                                            qnt: 0,
                                        })
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            productsStockAfterOperation: [
                                                ...newValue,
                                            ],
                                        }))
                                    }}
                                >
                                    +
                                </Button>
                            )}
                        </Stack>
                    ))}
                    <SubtitleHeader>{t('Stock')}</SubtitleHeader>
                    <Grid container spacing={2} sx={{ mb: '16px' }}>
                        <Grid item xs={12} md={6}>
                            <UnitTypeField
                                error={productErrorInfo.minStock}
                                onClick={() => {
                                    setProductErrorInfo((prev) => ({
                                        ...prev,
                                        minStock: '',
                                    }))
                                }}
                                label="Stock mínimo"
                                value={
                                    productInfo.minStock
                                        ? productInfo.minStock
                                        : 0
                                }
                                setValue={(v) => {
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        minStock: v,
                                    }))
                                }}
                                sx={{ width: '100%' }}
                                unitType={productInfo.unitType}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <UnitTypeField
                                error={productErrorInfo.idealStock}
                                onClick={() => {
                                    setProductErrorInfo((prev) => ({
                                        ...prev,
                                        idealStock: '',
                                    }))
                                }}
                                label="Stock ideal"
                                value={
                                    productInfo.idealStock
                                        ? productInfo.idealStock
                                        : 0
                                }
                                setValue={(v) => {
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        idealStock: v,
                                    }))
                                }}
                                sx={{ width: '100%' }}
                                unitType={productInfo.unitType}
                            />
                        </Grid>
                    </Grid>
                    {!isEditing &&
                        configs.systemBranch === SystemBranchEnum.NoBranch && (
                            <Grid container spacing={2} sx={{ mb: '16px' }}>
                                <Grid item xs={12} md={6}>
                                    <UnitTypeField
                                        error={productErrorInfo.stock}
                                        onClick={() => {
                                            setProductErrorInfo((prev) => ({
                                                ...prev,
                                                stock: '',
                                            }))
                                        }}
                                        label="Stock atual"
                                        value={
                                            productInfo.stock
                                                ? productInfo.stock
                                                : 0
                                        }
                                        setValue={(v) => {
                                            setProductInfo((prev) => ({
                                                ...prev,
                                                stock: v,
                                            }))
                                        }}
                                        sx={{ width: '100%' }}
                                        unitType={productInfo.unitType}
                                    />
                                </Grid>
                            </Grid>
                        )}
                    <SubtitleHeader sx={{ mt: '15px' }}>IVA</SubtitleHeader>
                    <Stack
                        direction="column"
                        sx={{
                            width: {
                                xs: '100%',
                                sm: '60%',
                                md: '45%',
                                lg: '30%',
                            },
                            mb: '15px',
                        }}
                    >
                        <FormControl>
                            <RadioGroup
                                row
                                onChange={(e) => {
                                    const { value } = e.target
                                    setProductInfo((prev) => ({
                                        ...prev,
                                        iva: Number(value),
                                    }))
                                }}
                                value={productInfo.iva}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <FormControlLabel
                                    value={IvaEnum.Exento}
                                    control={<Radio />}
                                    label={t('Exento')}
                                />
                                <FormControlLabel
                                    value={IvaEnum.Cinco}
                                    control={<Radio />}
                                    label="5%"
                                />
                                <FormControlLabel
                                    value={IvaEnum.Dez}
                                    control={<Radio />}
                                    label="10%"
                                />
                            </RadioGroup>
                        </FormControl>
                    </Stack>
                    {productInfo.iva !== IvaEnum.Exento && (
                        <TextField
                            sx={{
                                width: { xs: '100%', md: '47%' },
                                mb: '15px',
                            }}
                            label={t('Proporção do IVA (%)')}
                            value={productInfo.proporcaoIva ?? 100}
                            onChange={(e) => {
                                const value = Number(
                                    e.target.value.replace(/\D/g, '')
                                )
                                if (value > 100 || value < 1) return
                                setProductInfo((prev) => ({
                                    ...prev,
                                    proporcaoIva: value,
                                }))
                            }}
                            size="small"
                            type="number"
                            inputProps={{ min: 1, max: 100 }}
                        />
                    )}
                    <SubtitleHeader>{t('Otros parámetros')}</SubtitleHeader>
                    <Stack
                        flexDirection={{ xs: 'column', md: 'row' }}
                        sx={{ width: '100%', mb: '15px' }}
                    >
                        {/* <FormControlLabel
                    label="Mostrar iva en factura"
                    control={
                        <Checkbox
                            checked={productInfo.showIva}
                            onChange={(e) => {
                                setProductInfo((prev) => ({
                                    ...prev,
                                    showIva: e.target.checked,
                                }))
                            }}
                        />
                    }
                /> */}
                        <FormControlLabel
                            label={t('Permitir venta sin stock')}
                            control={
                                <Checkbox
                                    checked={productInfo.sellWithoutStock}
                                    onChange={(e) => {
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            sellWithoutStock: e.target.checked,
                                        }))
                                    }}
                                />
                            }
                        />
                        <FormControlLabel
                            label={t('Producto activo')}
                            control={
                                <Checkbox
                                    checked={productInfo.isAtive}
                                    onChange={(e) => {
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            isAtive: e.target.checked,
                                        }))
                                    }}
                                />
                            }
                        />
                        <FormControlLabel
                            label={t('Autosuma')}
                            control={
                                <Checkbox
                                    checked={productInfo.autoSum}
                                    onChange={(e) => {
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            autoSum: e.target.checked,
                                        }))
                                    }}
                                />
                            }
                        />
                        <FormControlLabel
                            label={t('Autoprecio')}
                            control={
                                <Checkbox
                                    checked={productInfo.autoPrice}
                                    onChange={(e) => {
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            autoPrice: e.target.checked,
                                        }))
                                    }}
                                />
                            }
                        />
                        <FormControlLabel
                            label={t('IVA obrigatório')}
                            control={
                                <Checkbox
                                    checked={productInfo.obrigatoryIva}
                                    onChange={(e) => {
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            obrigatoryIva: e.target.checked,
                                        }))
                                    }}
                                />
                            }
                        />
                        <FormControlLabel
                            label={t('Producto sin comision')}
                            control={
                                <Checkbox
                                    checked={productInfo.withoutComission}
                                    onChange={(e) => {
                                        setProductInfo((prev) => ({
                                            ...prev,
                                            withoutComission: e.target.checked,
                                        }))
                                    }}
                                />
                            }
                        />
                    </Stack>
                </>
            ),
            show: !isEditing || canUpdateProduct,
        },
        {
            component: (
                <>
                    <SubtitleHeader>{t('Imagen del producto')}</SubtitleHeader>
                    <GenericModal
                        open={openPreviewModal}
                        handleClose={setOpenPreviewModal}
                        handleSubmit={() => setOpenPreviewModal(false)}
                        title="Imagen"
                        textButton="Volver"
                    >
                        {productInfo.image ? (
                            <Box
                                component="img"
                                sx={{
                                    maxWidth: { xs: '300px', sm: '450px' },
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                                src={
                                    !productInfo.image?.isBase64
                                        ? getFileEndpoint(
                                              productInfo.image.content
                                          )
                                        : productInfo.image?.content
                                }
                            />
                        ) : (
                            <></>
                        )}
                    </GenericModal>
                    {!productInfo.image ? (
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                textTransform: 'none',
                                mb: '20px',
                                width: '240px',
                                p: '10px',
                            }}
                            onClick={() => setOpenFileModal(true)}
                        >
                            {t('Seleccionar imagen')}
                        </Button>
                    ) : (
                        <FileList
                            filename={productInfo.image.file.name}
                            handleClick={() => setOpenPreviewModal(true)}
                            handleDelete={() => {
                                setProductInfo((prev) => ({
                                    ...prev,
                                    image: null,
                                }))
                            }}
                        />
                    )}
                    <FileSelection
                        open={openFileModal}
                        handleClose={setOpenFileModal}
                        fileTypes="image/png, image/jpeg"
                        maxSize={5000000}
                        getFile={(file: File, content: string) => {
                            setOpenFileModal(false)
                            setProductInfo((prev) => ({
                                ...prev,
                                image: { file, content, isBase64: true },
                            }))
                        }}
                    />
                </>
            ),
            show: !isEditing || canUpdateProduct || canUpdateProductImage,
        },
    ]

    return (
        <>
            <GenericModal
                open={openGroupModal}
                handleClose={setOpenGroupModal}
                handleSubmit={() => {
                    handleCreateNewGroup(newGroupName)
                    setNewGroupName('')
                    setOpenGroupModal(false)
                }}
                title="Crear grupo"
                textButton="Crear"
            >
                <StringField
                    label="Nombre"
                    maxLength={100}
                    value={newGroupName}
                    setValue={setNewGroupName}
                    sx={{ width: '100%' }}
                />
            </GenericModal>
            <GenericModal
                open={openSubGroupModal}
                handleClose={setOpenSubGroupModal}
                handleSubmit={() => {
                    if (!productInfo.selectedGroup) return
                    handleCreateNewSubGroup(
                        newSubGroupName,
                        productInfo.selectedGroup.id
                    )
                    setNewSubGroupName('')
                    setOpenSubGroupModal(false)
                }}
                title="Crear nuevo subgrupo"
                textButton="Crear"
            >
                <Typography
                    sx={{
                        width: '100%',
                        fontSize: '16px',
                        fontWeight: '500',
                        mb: '15px',
                    }}
                >
                    {t('Grupo seleccionado')}: {productInfo.selectedGroup?.name}
                </Typography>
                <StringField
                    label="Nombre"
                    maxLength={100}
                    value={newSubGroupName}
                    setValue={setNewSubGroupName}
                    sx={{ width: '100%' }}
                />
            </GenericModal>
            <GenericModal
                open={openMarkModal}
                handleClose={setOpenMarkModal}
                handleSubmit={() => {
                    handleCreateNewMark(newMarkName)
                    setNewMarkName('')
                    setOpenMarkModal(false)
                }}
                title="Crear nueva marca"
                textButton="Crear"
            >
                <StringField
                    label="Nombre"
                    maxLength={100}
                    value={newMarkName}
                    setValue={setNewMarkName}
                    sx={{ width: '100%' }}
                />
            </GenericModal>
            <GenericModal
                open={openModelModal}
                handleClose={setOpenModelModal}
                handleSubmit={() => {
                    if (!productInfo.selectedMark) return
                    handleCreateNewModel(
                        newModelName,
                        productInfo.selectedMark.id
                    )
                    setNewModelName('')
                    setOpenModelModal(false)
                }}
                title="Crear nuevo modelo"
                textButton="Crear"
            >
                <Typography
                    sx={{
                        width: '100%',
                        fontSize: '16px',
                        fontWeight: '500',
                        mb: '15px',
                    }}
                >
                    {t('Marca seleccionada')}: {productInfo.selectedMark?.name}
                </Typography>
                <StringField
                    label="Nombre"
                    maxLength={100}
                    value={newModelName}
                    setValue={setNewModelName}
                    sx={{ width: '100%' }}
                />
            </GenericModal>
            <GenericModal
                open={openCreateNamedPriceModal}
                handleClose={setOpenCreateNamedPriceModal}
                handleSubmit={() => {
                    if (!newPriceName) {
                        setNewPriceNameError('Campo obrigatório')
                        return
                    }
                    if (!newPriceValue) {
                        setNewPriceValueError('Campo obrigatório')
                        return
                    }

                    const newNamedPrice = [...productInfo.namedPrice]

                    const existingPrice = newNamedPrice.find(
                        (np) => np.name === newPriceName
                    )

                    if (existingPrice && !editingPriceName) {
                        setNewPriceNameError(t('Valor já em uso'))
                        return
                    }

                    if (!editingPriceName) {
                        newNamedPrice.push({
                            name: newPriceName,
                            price: newPriceValue,
                            porcentGain: newPriceGain,
                            clients: [],
                        })
                    } else if (existingPrice) {
                        existingPrice.price = newPriceValue
                        existingPrice.porcentGain = newPriceGain
                    }

                    setProductInfo((prev) => ({
                        ...prev,
                        namedPrice: newNamedPrice,
                    }))
                    setEditingPriceName(false)
                    setNewPriceName('')
                    setNewPriceValue(0)
                    setNewPriceGain(0)
                    setOpenCreateNamedPriceModal(false)
                }}
                title="Crear nuevo precio"
                textButton="Confirmar"
            >
                <Stack mt={'5px'}>
                    <StringField
                        label="Número/Nombre"
                        maxLength={50}
                        value={newPriceName}
                        setValue={setNewPriceName}
                        error={newPriceNameError}
                        onClick={() => setNewPriceNameError('')}
                        disabled={!!editingPriceName}
                    />
                    <PorcentageField
                        label="Porcentagem de lucro"
                        value={newPriceGain}
                        setValue={(v) => {
                            let newSalePrice =
                                productInfo.buyPrice +
                                (productInfo.buyPrice * v) / 100

                            if (productInfo.coin?.hideDecimal) {
                                newSalePrice = Math.round(newSalePrice)
                            }

                            setNewPriceGain(v)
                            setNewPriceValue(newSalePrice)
                        }}
                        sx={{ mt: '15px' }}
                        error={newPriceValueError}
                        onClick={() => setNewPriceValueError('')}
                        allowNegative
                    />
                    <CurrencyField
                        label="Valor"
                        value={newPriceValue}
                        setValue={(v) => {
                            const newPercentageGain =
                                ((v - productInfo.buyPrice) * 100) /
                                productInfo.buyPrice

                            setNewPriceValue(v)
                            setNewPriceGain(newPercentageGain)
                        }}
                        coin={productInfo.coin}
                        sx={{ mt: '15px' }}
                        error={newPriceValueError}
                        onClick={() => setNewPriceValueError('')}
                    />
                </Stack>
            </GenericModal>
            <GenericModal
                open={openAddClientsNamedPriceModal}
                handleClose={setOpenAddClientsNamedPriceModal}
                handleSubmit={() => {}}
                title="Seleccionar clientes"
                textButton=""
                fullWidth
            >
                <GenericTable
                    title="b1d098e4-f8e8-4b5f-a52b-ef5cf0e6b70b"
                    withoutPaper
                    filters={[]}
                    search={search}
                    handleChangeSearch={(v) => {
                        setSearch(v)
                        if (v === search) return

                        clearTimeout(timeoutRef.current)
                        timeoutRef.current = setTimeout(() => {
                            getClients({ ...filters, page: 1 }, v)
                        }, 500)
                    }}
                    handleChangeSelectedFilter={(selectedFilter) => {}}
                    handleChangeSelectedValues={(values) => {
                        setSelectedClients(
                            values.map((value) => {
                                const foundValue = selectedClients.find(
                                    (sv) => sv.id === value
                                )
                                if (foundValue) return foundValue
                                else
                                    return clients.filter(
                                        (v) => v.id === value
                                    )[0]
                            })
                        )
                    }}
                    selectedValues={selectedClients.map((p) => p.id as number)}
                    identifier="id"
                    values={clients}
                    pagination={{
                        page: filters.page,
                        rows: filters.rows,
                        rowsCount: clients.length,
                        handleChangePagination: (page, rows) => {
                            getClients({ ...filters, page, rows }, search, true)
                        },
                    }}
                    columns={[
                        { name: 'Nombre', property: ['name'] },
                        { name: 'Documento', property: ['document'] },
                        {
                            name: 'Telefonos',
                            property: ['phoneNumbers'],
                            parser: (value) => value.join(', '),
                            hideSm: true,
                        },
                        {
                            name: 'Crédito',
                            property: ['allowCredit'],
                            customRender: (client: ClientInfo, value) => {
                                const creditText = client.allowCredit
                                    ? `${client.coin?.symbol} ${maskCurrency(
                                          client.creditValue,
                                          client.coin
                                      )}`
                                    : 'Cliente sem crédito'

                                const porcText = `${(
                                    (client.allowCredit
                                        ? client.creditUsed / client.creditValue
                                        : 0) * 100
                                ).toFixed(2)}%`

                                const text = `Crédito total: ${client.coin
                                    ?.symbol} ${maskCurrency(
                                    client.creditValue,
                                    client.coin
                                )}; Crédito utilizado: ${client.coin
                                    ?.symbol} ${maskCurrency(
                                    client.creditUsed,
                                    client.coin
                                )}`

                                return (
                                    <Stack direction={'column'} width={'100%'}>
                                        <Tooltip title={creditText}>
                                            <Typography
                                                fontSize={'0.875rem'}
                                                fontWeight={'bold'}
                                            >
                                                {creditText}
                                            </Typography>
                                        </Tooltip>
                                        <Stack
                                            direction={'row'}
                                            width={'100%'}
                                            alignItems={'center'}
                                        >
                                            <Tooltip title={porcText}>
                                                <Typography
                                                    fontSize={'0.75rem'}
                                                    color={'#71797E'}
                                                    minWidth={'45px'}
                                                >
                                                    {porcText}
                                                </Typography>
                                            </Tooltip>
                                            <Tooltip title={text}>
                                                <Box
                                                    sx={{
                                                        background: '#DCDCDC',
                                                        width: '60%',
                                                        height: '9px',
                                                        display: 'flex',
                                                        ml: '5px',
                                                        borderRadius: '6px',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            backgroundColor:
                                                                'warning.main',
                                                            width: `${
                                                                (client.creditUsed /
                                                                    client.creditValue) *
                                                                100
                                                            }%`,
                                                            borderRadius: '6px',
                                                        }}
                                                    ></Box>
                                                </Box>
                                            </Tooltip>
                                        </Stack>
                                    </Stack>
                                )
                            },
                        },
                        {
                            name: 'Activo',
                            property: ['isActive'],
                            customRender: (client: ClientInfo, value) => {
                                const text = t(value ? 'Sí' : 'No')
                                return (
                                    <Tooltip title={text}>
                                        <Typography
                                            fontSize={'0.875rem'}
                                            color={
                                                value
                                                    ? 'success.main'
                                                    : 'error.main'
                                            }
                                        >
                                            {text}
                                        </Typography>
                                    </Tooltip>
                                )
                            },
                            hideXs: true,
                        },
                    ]}
                    actions={[
                        {
                            name: 'Selecionar',
                            color: 'primary',
                        },
                    ]}
                    handleExecuteAction={(action) => {
                        if (action === 'Selecionar') {
                            const newNamedPrice = [...productInfo.namedPrice]
                            const index = newNamedPrice.findIndex(
                                (np) => np.name === selectedNamedPrice[0].name
                            )
                            if (index < 0) return
                            newNamedPrice[index].clients = [
                                ...selectedClients.map((c) => ({
                                    ...c,
                                    clientName: c.name || c.clientName,
                                    name: '',
                                })),
                            ]
                            setProductInfo((prev) => ({
                                ...prev,
                                namedPrice: newNamedPrice,
                            }))
                            setSelectedNamedPrice([])
                            setSelectedClients([])
                            setOpenAddClientsNamedPriceModal(false)
                        }
                    }}
                />
            </GenericModal>
            {isMassEditing && (
                <>
                    <StyledPaper sx={{ mb: '15px' }}>
                        <SubtitleHeader>
                            {t('Selecionar productos')}
                        </SubtitleHeader>
                        <ProductAdvancedSearch
                            localNavigation={true}
                            hideActionButtons
                        />
                    </StyledPaper>
                    <StyledPaper sx={{ mb: '15px' }}>
                        <SubtitleHeader>
                            {t('Campos para editar')}
                        </SubtitleHeader>
                        {massEditSelectedFields
                            .filter(
                                (massEditSelectedField) =>
                                    !massEditSelectedField.hide
                            )
                            .map((massEditSelectedField) => (
                                <FormControlLabel
                                    label={t(massEditSelectedField.field)}
                                    control={
                                        <Checkbox
                                            checked={
                                                massEditSelectedField.selected
                                            }
                                            onChange={(e) => {
                                                const newMassEditSelectedFields =
                                                    [...massEditSelectedFields]
                                                const foundMassEditSelectedField =
                                                    newMassEditSelectedFields.find(
                                                        (v) =>
                                                            v.name ===
                                                            massEditSelectedField.name
                                                    )
                                                if (foundMassEditSelectedField)
                                                    foundMassEditSelectedField.selected =
                                                        e.target.checked
                                                setMassEditSelectedFields(
                                                    newMassEditSelectedFields
                                                )
                                            }}
                                        />
                                    }
                                />
                            ))}
                    </StyledPaper>
                </>
            )}
            <StyledPaper>
                {components.filter((c) => c.show).map((c) => c.component)}
            </StyledPaper>
        </>
    )
}
